import {
  ArrowPathIcon,
  CheckCircleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PoliceReportAttestationStatus } from '@/services/api/generated/policeReportsService';

interface StatusCount {
  status: PoliceReportAttestationStatus;
  count: number;
}

interface PoliceOverviewHeaderProps {
  /** Count of unclear reports (legacy prop for compatibility) */
  unclearCount: number;
  /** Status counts for all reports */
  statusCounts?: Record<PoliceReportAttestationStatus, number>;
  /** Whether we have any data */
  hasData: boolean;
  /** Selection mode state */
  selectionMode: boolean;
  /** Toggle selection mode */
  onToggleSelectionMode: () => void;
  /** Refresh data */
  onRefresh: () => void;
  /** Create new report */
  onCreateReport: () => void;
}

export const PoliceOverviewHeader = ({
  unclearCount,
  statusCounts,
  hasData,
  selectionMode,
  onToggleSelectionMode,
  onRefresh,
  onCreateReport,
}: PoliceOverviewHeaderProps) => {
  // Calculate counts based on the new 3-bucket workflow
  const confirmedCount =
    (statusCounts?.attested_verified || 0) +
    (statusCounts?.verified_received || 0);

  const investigatingCount =
    (statusCounts?.unclear || 0) +
    (statusCounts?.attested_unverified || 0) +
    (statusCounts?.rejected_verified || 0) +
    (statusCounts?.inferred || 0);

  const totalActionableCount = confirmedCount + investigatingCount;

  // Determine primary message based on workflow status
  const getPrimaryMessage = () => {
    if (totalActionableCount === 0) return 'No police reports yet';
    if (confirmedCount > 0)
      return `${confirmedCount} report${confirmedCount === 1 ? '' : 's'} confirmed`;
    if (investigatingCount > 0)
      return `${investigatingCount} lead${investigatingCount === 1 ? '' : 's'} in investigation`;
    return `${totalActionableCount} reports tracked`;
  };

  const getMessageVariant = () => {
    if (totalActionableCount === 0) return 'neutral';
    if (confirmedCount > 0) return 'complete'; // Blue - we found the report
    return 'neutral'; // Everything else is just normal workflow
  };

  const messageVariant = getMessageVariant();

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Title and primary status message */}
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900">Police Reports</h1>

            {/* Focus on actionable items */}
            <div
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium',
                messageVariant === 'complete' &&
                  'bg-blue-50 text-blue-700 border border-blue-200',
                messageVariant === 'neutral' &&
                  'bg-gray-50 text-gray-600 border border-gray-200',
              )}
            >
              {messageVariant === 'complete' && (
                <CheckCircleIcon className="h-4 w-4" />
              )}
              <span>{getPrimaryMessage()}</span>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              className="text-xs"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Refresh
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={onCreateReport}
              className="text-xs"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Report
            </Button>
          </div>
        </div>

        {/* Quick stats - only show if we have actionable reports */}
        {totalActionableCount > 0 && (
          <div className="mt-3 flex items-center text-xs text-gray-500 space-x-4">
            {confirmedCount > 0 && <span>{confirmedCount} confirmed</span>}
            {investigatingCount > 0 && (
              <span>{investigatingCount} investigating</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
